import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import './App.css'
import Header from './components/Header/Header'
import VideoBanner from './components/VideoBanner/VideoBanner'
import VehicleInfo from './components/VehicleInfo/VehicleInfo'
import Summary from './components/Summary/Summary'
import Details from './components/Details/Details'
import FluidSpecs from './components/FluidSpecs/FluidSpecs'
import MechanicInfo from './components/MechanicInfo/MechanicInfo'
import BranchContact from './components/BranchContact/BranchContact'
import AIAssistant, { AIAssistantRef } from './components/AIAssistant/AIAssistant'
import VideoModal from './components/VideoModal/VideoModal'
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner'
import FramesStrip from './components/FramesStrip/FramesStrip'
import FramesCarousel from './components/FramesCarousel/FramesCarousel'
import { getOrderReportDetail, getOrderFrames, reportViewed, getFluidSpecs } from './services/orderService'
import { OrderReportDetail, StripItem, VideoFrame, FluidItem } from './types'

function App() {
  const { t, i18n } = useTranslation()
  const [orderData, setOrderData] = useState<OrderReportDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [frames, setFrames] = useState<VideoFrame[]>([])
  const [framesLoading, setFramesLoading] = useState(true)
  const [fluidSpecs, setFluidSpecs] = useState<FluidItem[] | null>(null)
  const [carouselIndex, setCarouselIndex] = useState<number | null>(null)
  const aiAssistantRef = useRef<AIAssistantRef>(null)

  const searchParams = new URLSearchParams(window.location.search)
  const orderId = searchParams.get('id') || null
  // ?preview=1 / ?preview=true — предпросмотр дилером или админом, не считается просмотром клиента
  const previewParam = searchParams.get('preview')
  const isPreview = previewParam === '1' || previewParam === 'true'

  useEffect(() => {
    if (!orderId) {
      console.error('[ERROR] Missing orderId in URL params')
      setError(t('app.error_missing_id'))
      setLoading(false)
      return
    }

    const fetchOrderData = async () => {
      try {
        setLoading(true)
        setError(null)
        let data = await getOrderReportDetail(orderId)
        // отчёт показываем на языке ДЦ: переключаем язык и перезапрашиваем с нужным Accept-Language
        const branchLocale = data.branch?.locale
        if (branchLocale && branchLocale !== i18n.language && i18n.hasResourceBundle(branchLocale, 'translation')) {
          await i18n.changeLanguage(branchLocale)
          data = await getOrderReportDetail(orderId)
        }
        setOrderData(data)
        console.log(`[INFO] Order report loaded — orderId: ${orderId}`)
      } catch (err) {
        console.error(`[ERROR] Failed to load order report — orderId: ${orderId}`, err)
        setError(t('app.error_load_failed'))
      } finally {
        setLoading(false)
      }
    }

    const fetchFrames = async () => {
      try {
        setFramesLoading(true)
        const data = await getOrderFrames(orderId)
        setFrames(data)
      } catch (err) {
        console.error(`[ERROR] Failed to load frames — orderId: ${orderId}`, err)
      } finally {
        setFramesLoading(false)
      }
    }

    reportViewed(orderId, isPreview)
    fetchOrderData()
    fetchFrames()
  }, [orderId])

  useEffect(() => {
    const vin = orderData?.vin
    if (!vin) return
    getFluidSpecs(vin).then(data => setFluidSpecs(data))
  }, [orderData?.vin])

  const stripItems = useMemo<StripItem[]>(() => {
    const frameItems: StripItem[] = frames.map(f => ({
      id: `frame-${f.frameIndex}`,
      url: f.url,
      kind: 'frame',
    }))
    const defectItems: StripItem[] = (orderData?.defectImgs ?? []).map(d => ({
      id: d.id,
      url: d.imgUrl,
      kind: 'defect',
    }))
    return [...frameItems, ...defectItems]
  }, [frames, orderData?.defectImgs])

  const handleVideoPlay = () => {
    if (orderData?.videoUrl) {
      if (aiAssistantRef.current) {
        aiAssistantRef.current.close()
      }
      setCarouselIndex(null)
      setIsVideoModalOpen(true)
    }
  }

  const handleItemClick = (index: number) => {
    if (aiAssistantRef.current) {
      aiAssistantRef.current.close()
    }
    setCarouselIndex(index)
  }

  const handleCloseVideo = () => {
    setIsVideoModalOpen(false)
  }

  const parseDateTime = (dateTimeString: string) => {
    const [datePart, timePart] = dateTimeString.split(', ')
    return { date: datePart, time: timePart || '' }
  }

  if (loading) {
    return (
      <div className="app">
        <div className="app-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <LoadingSpinner size="large" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="app">
        <div className="app-container">
          <div style={{ padding: '24px', textAlign: 'center', color: '#d32f2f' }}>
            <p>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!orderData) {
    return null
  }

  const { date, time } = parseDateTime(orderData.dateTime)

  return (
    <div className="app">
      <div className="app-container">
        <Header branch={orderData.branch} />
        <VideoBanner
          videoThumbnail={orderData.videoUrl ? undefined : undefined}
          onPlay={handleVideoPlay}
          showPlayButton={!isVideoModalOpen}
        />
        <FramesStrip
          items={stripItems}
          loading={framesLoading}
          onItemClick={handleItemClick}
        />
        <VehicleInfo
          year={orderData.carYear ?? undefined}
          make={orderData.carModel ? orderData.carModel.split(' ')[0] : undefined}
          model={orderData.carModel ? orderData.carModel.split(' ').slice(1).join(' ') : undefined}
          vin={orderData.vin}
          date={date}
          time={time}
        />
        <Summary text={orderData.orderSummary} />
        <Details componentReports={orderData.componentReports} />
        {fluidSpecs !== null && <FluidSpecs items={fluidSpecs} />}
        <MechanicInfo
          mechanicName={orderData.mechanic}
          companyName={orderData.branch ? `${t('mechanic_info.role')} · ${orderData.branch.name}` : orderData.company}
        />
        <BranchContact branch={orderData.branch} />
        <AIAssistant
          ref={aiAssistantRef}
          speechText={orderData.speechText}
          audioUrl={orderData.orderNumber
            ? `${import.meta.env.VITE_S3_BASE_URL ?? 'https://verrafiedfs-data.s3.amazonaws.com'}/videos/ml/${orderData.orderNumber}_meta/${orderData.orderNumber}.mp3`
            : undefined
          }
        />
        {orderData.videoUrl && (
          <VideoModal
            videoUrl={orderData.videoUrl}
            muted={orderData.branch?.muteVideo ?? false}
            isOpen={isVideoModalOpen}
            onClose={handleCloseVideo}
          />
        )}
        {carouselIndex !== null && (
          <FramesCarousel
            frames={stripItems}
            initialIndex={carouselIndex}
            onClose={() => setCarouselIndex(null)}
          />
        )}
      </div>
    </div>
  )
}

export default App
